import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { createPortal } from "react-dom";
import "./style.css";

const DefaultSearchIcon = () => (
  <div className="search-autocomplete-item-img-placeholder" aria-hidden>
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
    </svg>
  </div>
);

const SuggestionThumbImage = ({ src }) => {
  const [failed, setFailed] = useState(false);
  if (failed) return <DefaultSearchIcon />;
  return (
    <img
      src={src}
      alt=""
      className="search-autocomplete-item-img"
      onError={() => setFailed(true)}
    />
  );
};

/**
 * SearchAutocomplete Component
 * Provides autocomplete suggestions for search inputs based on keywords stored in the database
 *
 * Props:
 * - value: Current search value
 * - onChange: Callback when value changes
 * - onSelect: Callback when a suggestion is selected
 * - onSearch: Callback when search is triggered (Enter key or suggestion click)
 * - placeholder: Input placeholder text
 * - className: Additional CSS class for the input
 * - disabled: Whether the input is disabled
 * - fetchSuggestions: Function to fetch suggestions (should return Promise<Array>)
 * - debounceMs: Debounce delay in milliseconds (default: 300)
 * - minChars: Minimum characters to trigger suggestions (default: 2)
 * - maxSuggestions: Max number of suggestions to show (default: 6)
 * - suppressSuggestions: When true, never show dropdown (e.g. on results page when search matches URL)
 *
 * Ref: Forwarded to the inner input element (supports .focus() etc.)
 */
const SearchAutocomplete = forwardRef(({
  value,
  onChange,
  onSelect,
  onSearch,
  placeholder = "Search...",
  className = "",
  disabled = false,
  fetchSuggestions,
  debounceMs = 300,
  minChars = 2,
  maxSuggestions = 6,
  suppressSuggestions = false,
}, ref) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const debounceTimer = useRef(null);
  const requestIdRef = useRef(0);
  const justSelectedRef = useRef(false);
  const hasFocusedRef = useRef(false);
  const suppressSuggestionsRef = useRef(suppressSuggestions);

  // Forward ref to the actual input so parent can call .focus()
  useImperativeHandle(ref, () => inputRef.current, []);

  suppressSuggestionsRef.current = suppressSuggestions;

  // Same slug as backend: "Smart watch" and "smart watch" both -> "smart-watch"
  const keywordToSlug = (str) => {
    if (!str || typeof str !== "string") return "";
    return str
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  // Update dropdown position from input position (for portal - position: fixed)
  const updateDropdownPosition = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  };

  // Fetch suggestions when input value changes
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    if (!value || value.trim().length < minChars) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    debounceTimer.current = setTimeout(async () => {
      const queryForThisRequest = value.trim();
      const requestId = ++requestIdRef.current;
      try {
        setLoading(true);
        const results = await fetchSuggestions(queryForThisRequest, maxSuggestions);
        // Ignore stale/in-flight responses from previous input values.
        if (requestId !== requestIdRef.current) return;
        const raw = results || [];
        // Deduplicate by slug so "Smart watch" and "smart watch" show once (same slug: smart-watch)
        const seenSlug = new Set();
        const list = [];
        for (const s of raw) {
          const slug = keywordToSlug(s.keyword);
          if (!slug || seenSlug.has(slug)) continue;
          seenSlug.add(slug);
          list.push(s);
          if (list.length >= maxSuggestions) break;
        }
        setSuggestions(list);
        if (justSelectedRef.current) {
          justSelectedRef.current = false;
          setShowSuggestions(false);
        } else if (!suppressSuggestionsRef.current && hasFocusedRef.current) {
          setShowSuggestions(list.length > 0);
          if (list.length > 0) {
            updateDropdownPosition();
          }
        } else {
          setShowSuggestions(false);
        }
      } catch (error) {
        // Ignore stale/in-flight errors
        if (requestId !== requestIdRef.current) return;
        console.error("[SearchAutocomplete] Error fetching suggestions:", error);
        setSuggestions([]);
        setShowSuggestions(false);
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    }, debounceMs);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [value, fetchSuggestions, debounceMs, minChars, maxSuggestions]);

  // When suppressSuggestions is true (e.g. on results page), close dropdown and reset focus state
  useEffect(() => {
    if (suppressSuggestions) {
      setShowSuggestions(false);
      hasFocusedRef.current = false;
    }
  }, [suppressSuggestions]);

  // Update dropdown position when open and on scroll/resize (for portal)
  useEffect(() => {
    if (!showSuggestions || suggestions.length === 0) return;
    updateDropdownPosition();
    const onScrollOrResize = () => updateDropdownPosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [showSuggestions, suggestions.length]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleInputChange = (e) => {
    setSelectedIndex(-1);
    onChange(e);
  };

  const handleSuggestionClick = (suggestion) => {
    justSelectedRef.current = true;
    setShowSuggestions(false);
    const syntheticEvent = {
      target: {
        name: inputRef.current?.name,
        value: suggestion.keyword,
      },
    };
    onChange(syntheticEvent);
    onSelect && onSelect(suggestion);

    // Trigger search immediately when a suggestion is clicked
    if (onSearch) {
      setTimeout(() => onSearch(suggestion.keyword), 0);
    }
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === "Enter" && onSearch) {
        onSearch(value);
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSuggestionClick(suggestions[selectedIndex]);
        } else if (onSearch) {
          onSearch(value);
          setShowSuggestions(false);
        }
        break;
      case "Escape":
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  // Amazon-style: query in normal weight, completion (rest of keyword) in bold
  const renderSuggestionText = (keyword, query) => {
    const text = (keyword || "").replace(/-/g, " ");
    if (!text) return null;
    if (!query || !query.trim()) return text;
    const q = query.trim().toLowerCase();
    const i = text.toLowerCase().indexOf(q);
    if (i === -1) return text;
    const before = text.slice(0, i);
    const match = text.slice(i, i + q.length);
    const after = text.slice(i + q.length);
    return (
      <>
        {before}
        {match}
        {after ? <strong className="search-autocomplete-completion">{after}</strong> : null}
      </>
    );
  };

  const dropdownContent = !suppressSuggestions && showSuggestions && suggestions.length > 0 && (
    <div
      ref={suggestionsRef}
      className="search-autocomplete-dropdown search-autocomplete-dropdown-portal"
      style={{
        position: "fixed",
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        width: Math.max(dropdownPosition.width, 280),
        zIndex: 99999,
      }}
    >
      {suggestions.map((suggestion, index) => (
        <div
          key={index}
          className={`search-autocomplete-item ${
            index === selectedIndex ? "search-autocomplete-item-selected" : ""
          }`}
          onClick={() => handleSuggestionClick(suggestion)}
          onMouseEnter={() => setSelectedIndex(index)}
        >
          <div className="search-autocomplete-item-thumb">
            {suggestion.imageUrl ? (
              <SuggestionThumbImage src={suggestion.imageUrl} />
            ) : (
              <DefaultSearchIcon />
            )}
          </div>
          <div className="search-autocomplete-item-keyword">
            {renderSuggestionText(suggestion.keyword, value)}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="search-autocomplete-wrapper">
      <input
        ref={inputRef}
        type="text"
        className={`${className} ${loading ? "search-autocomplete-loading" : ""}`}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          hasFocusedRef.current = true;
          justSelectedRef.current = false;
          if (suggestions.length > 0 && !suppressSuggestionsRef.current) {
            setShowSuggestions(true);
            updateDropdownPosition();
          }
        }}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
      />
      {dropdownContent && createPortal(dropdownContent, document.body)}
    </div>
  );
});

SearchAutocomplete.displayName = "SearchAutocomplete";

export default SearchAutocomplete;
